/**
 * Offboarding flow container. Uses currentState:
 * 0 = closed (no modals), 1 = Acknowledgment modal, 2 = Survey modal.
 */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import OffboardAcknowledgementModal from './OffboardAcknowledgementModal';
import OffboardingSurveyModal from './OffboardingSurveyModal';
import Modal from 'tailwind-components/Modal/Modal';
import Button from 'tailwind-components/Button/Button';
import useOffboarding, { getModalTitle } from './useOffboarding';

export default function OffboardingModal({
  onClose,
  source,
  selectedAgentId,
  hideReleaseApprovalQuestion,
  refreshUser,
  onSuccess
}) {
  const {
    currentStep,
    setCurrentStep,
    isLoading,
    formData,
    setFormData,
    isFormValid,
    handleSend
  } = useOffboarding({
    onClose,
    source,
    selectedAgentId,
    hideReleaseApprovalQuestion,
    refreshUser,
    onSuccess
  });

  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  return (
    <Modal
      active
      contentRef={contentRef}
      isLoading={isLoading}
      title={getModalTitle(currentStep, isLoading)}
      size='large'
      showScrollbar
      closeModal={onClose}
      footer={
        <Footer
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          isFormValid={isFormValid}
          isLoading={isLoading}
          handleSend={handleSend}
        />
      }
    >
      {currentStep === 1 && (
        <OffboardAcknowledgementModal
          formData={formData}
          setFormData={setFormData}
          source={source}
        />
      )}
      {currentStep === 2 && (
        <OffboardingSurveyModal
          formData={formData}
          setFormData={setFormData}
          source={source}
          hideReleaseApprovalQuestion={hideReleaseApprovalQuestion}
        />
      )}
    </Modal>
  );
}

OffboardingModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  source: PropTypes.oneOf(['agencyTable', 'agentInfo']),
  selectedAgentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideReleaseApprovalQuestion: PropTypes.bool,
  refreshUser: PropTypes.func,
  onSuccess: PropTypes.func
};

function Footer({
  currentStep,
  setCurrentStep,
  isFormValid,
  isLoading,
  handleSend
}) {
  if (currentStep === 1) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          text='Continue'
          variant='primary'
          onClick={() => setCurrentStep(2)}
          isDisabled={!isFormValid}
        />
      </div>
    );
  }

  if (currentStep === 2) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          text='Back'
          variant='secondary'
          onClick={() => setCurrentStep(1)}
          style={{ marginRight: 10 }}
          isDisabled={isLoading}
        />
        <Button
          text='Send'
          variant='primary'
          onClick={handleSend}
          isDisabled={!isFormValid || isLoading}
          isLoading={isLoading}
        />
      </div>
    );
  }
}

Footer.propTypes = {
  currentStep: PropTypes.number.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  isFormValid: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  handleSend: PropTypes.func.isRequired
};
